import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon } from 'lucide-react';
const TESTIMONIALS = [{
  quote: '“Por primera vez sentí que seguía un plan pensado para esta etapa, no una dieta genérica bajada de internet.”',
  name: 'Laura',
  age: '52 años',
  location: 'CABA'
}, {
  quote: '“No tengo mucho tiempo entre el trabajo y la casa, así que lo que más agradecí fue que las rutinas de H.E.R. Movement duraran quince minutos y no una hora.”',
  name: 'María',
  age: '49 años',
  location: 'Rosario, Santa Fe'
}, {
  quote: '“Empecé sin muchas expectativas, la verdad. A la segunda semana ya tenía el tracker marcado casi todos los días, y eso para mí ya es mucho.”',
  name: 'Carolina',
  age: '47 años',
  location: 'Córdoba'
}, {
  quote: '“El Recetario me sacó el problema de pensar qué cocinar todas las noches.”',
  name: 'Gabriela',
  age: '50 años',
  location: 'Mendoza'
}, {
  quote: '“Lo de Noche en Calma me ayudó más de lo que pensé. Dormir mejor cambió bastante mi ánimo durante el día.”',
  name: 'Marcela',
  age: '54 años',
  location: 'La Plata'
}, {
  quote: '“Probé bastantes cosas antes y siempre terminaba abandonando a la semana. Con H.E.R. llegué al día 21 completo, y creo que fue porque no me pedía perfección, solo ir avanzando.”',
  name: 'Silvia',
  age: '48 años',
  location: 'Mar del Plata'
}, {
  quote: '“simple, directo, sin vueltas. justo lo que necesitaba.”',
  name: 'Andrea',
  age: '51 años',
  location: 'Tucumán'
}, {
  quote: '“Tenía mil pestañas abiertas buscando información suelta. Acá encontré todo junto y ordenado, eso solo ya valió la pena.”',
  name: 'Patricia',
  age: '46 años',
  location: 'Neuquén'
}];
type Testimonial = (typeof TESTIMONIALS)[number];
const AUTOPLAY_DELAY = 3000;
const INTERACTION_PAUSE = 8000;
const SWIPE_THRESHOLD = 40;
function TestimonialCard({
  testimonial
}: {
  testimonial: Testimonial;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.5,
    ease: 'easeInOut'
  }} className="card-premium relative overflow-hidden p-7 md:p-9" data-id="element-787" data-mp="3sgox4mqftj4n">
      <div className="space-y-7" data-id="element-788" data-mp="dcyhy5rl9pni">
        <p className="font-body text-[17px] leading-[1.7] italic text-foreground/90 md:text-[19px]" data-id="element-789" data-mp="ddzvcgw2uo8e">
          {testimonial.quote}
        </p>
        <div className="border-t border-border/50 pt-5" data-id="element-790" data-mp="i9yoehscouf5">
          <p className="font-display text-lg font-bold text-gold-light" data-id="element-791" data-mp="3guq6tbafhthh">{testimonial.name}</p>
          <p className="mt-1 font-body text-sm text-foreground/55" data-id="element-792" data-mp="3lrymn583tlfk">
            {testimonial.age} · {testimonial.location}
          </p>
        </div>
      </div>
    </motion.div>;
}
export function TestimonialsSection() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const pauseUntilRef = useRef(0);
  const pointerStartXRef = useRef<number | null>(null);
  const pauseAutoplay = () => {
    pauseUntilRef.current = Date.now() + INTERACTION_PAUSE;
  };
  const goTo = (index: number) => {
    pauseAutoplay();
    setActiveTestimonial(index);
  };
  const moveBy = (direction: 1 | -1) => {
    pauseAutoplay();
    setActiveTestimonial(current => (current + direction + TESTIMONIALS.length) % TESTIMONIALS.length);
  };
  useEffect(() => {
    const interval = window.setInterval(() => {
      if (Date.now() >= pauseUntilRef.current) {
        setActiveTestimonial(current => (current + 1) % TESTIMONIALS.length);
      }
    }, AUTOPLAY_DELAY);
    return () => window.clearInterval(interval);
  }, []);
  const current = TESTIMONIALS[activeTestimonial];
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary) return;
    pauseAutoplay();
    pointerStartXRef.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartXRef.current === null) return;
    const distance = event.clientX - pointerStartXRef.current;
    pointerStartXRef.current = null;
    if (Math.abs(distance) >= SWIPE_THRESHOLD) {
      moveBy(distance < 0 ? 1 : -1);
    }
  };
  return <section className="bg-gradient-section px-4 py-12 md:px-8 md:py-16" style={{
    paddingTop: "0px"
  }} data-id="element-793" data-mp="2im4goma7j20w">
      <div className="mx-auto max-w-[1400px] space-y-10 md:space-y-12" data-id="element-794" data-mp="23tb0eyn42hv6">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="space-y-3 text-center" data-id="element-795" data-mp="28sfd3z98uxq9">
          <p className="text-secondary text-[10px] font-bold uppercase tracking-[0.24em] md:text-xs" style={{
          fontSize: '13px',
          paddingTop: "20px"
        }} data-id="element-796" data-mp="1kwt19w5xnfow">
            Lo que cuentan mujeres que hicieron H.E.R.
          </p>
          <h2 className="font-display text-3xl font-black leading-tight md:text-4xl" data-id="element-797" data-mp="3fb3qw6ucqdvq">
            Sus propias <span className="text-gradient-gold" data-id="element-798" data-mp="3k8m3c77kvb8h">palabras</span>
          </h2>
          <div className="flex items-center justify-center gap-2 pt-1" data-id="element-799" data-mp="18mtqt1w1a0ay">
            <div className="flex gap-0.5" role="img" aria-label="Calificación de 5 de 5 estrellas" data-id="element-800" data-mp="13nh0g75yeb3m">
              {[...Array(5)].map((_, index) => <StarIcon key={index} className="h-4 w-4 text-secondary fill-secondary" data-id="element-801" data-mp="1idfz0b1f7ojm" data-mpi={index} />)}
            </div>
            <p className="font-body text-[13px] text-foreground/55 md:text-sm" style={{
            fontSize: '16px'
          }} data-id="element-802" data-mp="1djtvb8d8iuzy">
              Cada vez más mujeres eligen H.E.R.
            </p>
          </div>
        </motion.div>

        <div className="mx-auto max-w-2xl" data-id="element-803" data-mp="21aphnpyx8xzt">
          <div className="relative touch-pan-y" onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerCancel={() => {
          pointerStartXRef.current = null;
        }} aria-roledescription="carrusel" aria-label="Testimonios de mujeres que hicieron H.E.R." data-id="element-804" data-mp="219eftvp1e0bc">
            <AnimatePresence mode="wait" initial={false} data-id="element-805" data-mp="1yeqd2917oht">
              {current && <TestimonialCard key={current.name} testimonial={current} data-id="element-806" data-mp="21zur9i9jcpfg" />}
            </AnimatePresence>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2" aria-label="Elegir testimonio" data-id="element-807" data-mp="26yfs9jwm3d4y">
            {TESTIMONIALS.map((testimonial, index) => <button key={testimonial.name} type="button" onClick={() => goTo(index)} aria-label={`Ver testimonio de ${testimonial.name}`} aria-current={index === activeTestimonial ? 'true' : undefined} className="flex h-6 items-center justify-center rounded-sm px-0.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-808" data-mp="2az80c9tqzbzn" data-mpi={index}>
              <span aria-hidden="true" className={`rounded-full transition-all duration-300 ${index === activeTestimonial ? 'h-1.5 w-6 bg-[#C8A870]' : 'h-1.5 w-1.5 bg-[#C8A870]/25'}`} data-id="element-809" data-mp="2uoijjxspcf2i" />
            </button>)}
          </div>
        </div>
      </div>
    </section>;
}